import axiosInstance from "../../../services/axiosInstance";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import routes from "../../../constants/routes.json";
import FormHeader from "../../../components/retail_form/sections/header";
import ViewLicenseInformation from "./sections/view_license_information";
import ViewStoreInformation from "./sections/view_store_information";
import ViewCorporationInformation from "./sections/view_corporation_information";
import ViewShopperEngagement from "./sections/view_shopper_engagement";
import ViewStoreAdGroupInformation from "./sections/view_store_ad_group_information";
import ViewInsuranceInformation from "./sections/view_insurance_information";
import ViewOfferingsAtStore from "./sections/view_offerings_at_store";
import ViewIGADistributionCenter from "./sections/view_iga_distribution_center";
import EditIcon from "@mui/icons-material/Edit";
import SkeletonViewStoreInformation from "./sections/skeleton/SkeletonViewStoreInformation";
import ViewLicenseInformationSkeleton from "./sections/skeleton/SkeletonViewLicenseInformation";
import OwnerInformation from "./sections/view_owner_information";
import OwnerInformationSkeleton from "./sections/skeleton/OwnerInformationSkeleton";
import ViewCorporationInformationSkeleton from "./sections/skeleton/ViewCorporationInformationSkeleton";
import ViewShopperEngagementSkeleton from "./sections/skeleton/ViewShopperEngagementSkeleton";
import { useEffect, useState } from "react";
import { Grid, Typography, Paper, Box, Tooltip, Button } from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../../../hooks/useAuth";
import { FormData } from "../../../pages/retial_form/retail_application_form";
import { showLoader } from "../../../components/loader/show_loader";
import { motion } from "framer-motion";

const ViewRetailerApplicationForm = () => {
  // States
  const [formData, setFormData] = useState<FormData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [emailReceivedDone, setEmailReceivedDone] = useState<boolean>(false);

  // Hooks
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isAuthenticated = useAuth();

  // API calls
  useEffect(() => {
    if (id) {
      const loadFormData = async () => {
        try {
          setLoading(true);
          const response = await axiosInstance.get(`/api/v1/retail/${id}`);

          if (response.data) {
            const {
              licenseData: {
                licenseType,
                applicationDate,
                submittedBy,
                storeFormat,
                otherStoreFormat,
                distributionCenter,
                otherDistributionCenter,
                numStoresOwned,
                numStoreApplied,
                wholesalerContact,
                wholesalerEmail,
              },
              storeInformationData: {
                storeName,
                storeAddress,
                storeCity,
                storeState,
                storeZip,
                storeBillingAddress,
                storeBillingCity,
                storeBillingState,
                storeBillingZip,
                poBox,
                poBoxCity,
                poBoxState,
                poBoxZip,
                storePhone,
                storeEmail,
                storeWebsite,
                numCheckouts,
                numSelfCheckouts,
                posSystem,
              },
              ownerInformationData: {
                primaryOwnerName,
                primaryOwnerTitle,
                primaryOwnerEmail,
                primaryOwnerMobile,
                additionalOwnerName,
                additionalOwnerTitle,
                additionalOwnerEmail,
                additionalOwnerMobile,
                managerName,
                managerPhone,
                managerEmail,
              },
              corporationInformationData: {
                legalCorpName,
                corpAddress,
                corpCity,
                corpState,
                corpZip,
              },
              shopperEngagementData: {
                websiteDeveloper,
                websiteDeveloperCompany,
                ecommerceStorePickup,
                ecommerceStorePickupCompany,
                ecommerceDelivery,
                ecommerceDeliveryCompany,
                loyaltyProgram,
                loyaltyProgramCompany,
                socialMedia,
                socialMediaManager,
                cocaColaBottler,
              },
              storeAdGroupInformationData: {
                adGroupName,
                adGroupManager,
                digitalCirculars,
                printCircular,
                numPrintAdsWeekly,
              },
              insuranceInformationData: {
                insuranceAgentName,
                insuranceCompanyName,
                insuranceAddress,
                insuranceCity,
                insuranceState,
                insuranceZip,
                insurancePhone,
                insuranceEmail,
              },
              offeringsAtStoreData: {
                offeringPetroleum,
                offeringPreparedFood,
                offeringServiceDeli,
                offeringBakery,
                offeringDeli,
                offeringEVCharging,
                carryIGAProducts,
                otherOfferings,
                interiorSquareFootage,
              },
              igaDistributionCenterData: { ldcSubmittedBy, ldcSubmittedDate },
              emailReceived,
            } = response.data.data;

            setFormData({
              licenseType,
              applicationDate,
              submittedBy,
              storeFormat,
              otherStoreFormat,
              distributionCenter,
              otherDistributionCenter,
              numStoresOwned,
              numStoreApplied,
              wholesalerContact,
              wholesalerEmail,
              storeName,
              storeAddress,
              storeCity,
              storeState,
              storeZip,
              storeBillingAddress,
              storeBillingCity,
              storeBillingState,
              storeBillingZip,
              poBox,
              poBoxCity,
              poBoxState,
              poBoxZip,
              storePhone,
              storeEmail,
              storeWebsite,
              numCheckouts,
              numSelfCheckouts,
              posSystem,
              primaryOwnerName,
              primaryOwnerTitle,
              primaryOwnerEmail,
              primaryOwnerMobile,
              additionalOwnerName,
              additionalOwnerTitle,
              additionalOwnerEmail,
              additionalOwnerMobile,
              managerName,
              managerPhone,
              managerEmail,
              legalCorpName,
              corpAddress,
              corpCity,
              corpState,
              corpZip,
              websiteDeveloper,
              websiteDeveloperCompany,
              ecommerceStorePickup,
              ecommerceStorePickupCompany,
              ecommerceDelivery,
              ecommerceDeliveryCompany,
              loyaltyProgram,
              loyaltyProgramCompany,
              socialMedia,
              socialMediaManager,
              cocaColaBottler,
              adGroupName,
              adGroupManager,
              digitalCirculars,
              printCircular,
              numPrintAdsWeekly,
              insuranceAgentName,
              insuranceCompanyName,
              insuranceAddress,
              insuranceCity,
              insuranceState,
              insuranceZip,
              insurancePhone,
              insuranceEmail,
              offeringPetroleum,
              offeringPreparedFood,
              offeringServiceDeli,
              offeringBakery,
              offeringDeli,
              offeringEVCharging,
              carryIGAProducts,
              otherOfferings,
              interiorSquareFootage,
              ldcSubmittedBy,
              ldcSubmittedDate,
            });

            setEmailReceivedDone(emailReceived);
          } else {
            console.error("Invalid API response structure:", response.data);
          }
        } catch (err) {
          setError("Failed to load data. Please try again.");
        } finally {
          setLoading(false);
        }
      };

      loadFormData();
    }
  }, [id]);

  if (loading) {
    return (
      <Paper
        elevation={9}
        sx={{
          p: 4,
          maxWidth: 900,
          margin: "auto",
          position: "relative",
        }}
      >
        {/* Form Header */}
        <motion.div
          initial={{ filter: "blur(20px)", opacity: 0 }}
          animate={{ filter: "blur(0px)", opacity: 1 }}
          transition={{ duration: 1 }}
          className="text-xl text-center sm:text-4xl font-bold tracking-tighter md:text-6xl md:leading-[4rem]"
        >
          <FormHeader />
        </motion.div>
        <Grid container spacing={3}>
          <ViewLicenseInformationSkeleton />
          <SkeletonViewStoreInformation />
          <OwnerInformationSkeleton />
          <ViewCorporationInformationSkeleton />
          <ViewShopperEngagementSkeleton />
        </Grid>
        {/* Include other skeletons */}
      </Paper>
    );
  }

  if (error) {
    return (
      <Box textAlign="center" margin={3}>
        <Typography variant="h6" color={"white"} bgcolor={"red"} p={0.5}>
          {error}
        </Typography>
      </Box>
    );
  }

  if (!formData) {
    return (
      <Box textAlign="center" margin={3}>
        <Typography variant="h6" color={"white"} bgcolor={"red"} p={0.5}>
          No data available.
        </Typography>
      </Box>
    );
  }

  return (
    <motion.div
      initial={{ filter: "blur(20px)", opacity: 0 }}
      animate={{ filter: "blur(0px)", opacity: 1 }}
      transition={{ duration: 0.5 }}
      className="text-xl text-center sm:text-4xl font-bold tracking-tighter md:text-6xl md:leading-[4rem]"
    >
      <Paper
        elevation={9}
        sx={{
          p: 4,
          maxWidth: 900,
          margin: "auto",
          position: "relative",
        }}
      >
        {isAuthenticated && (
          <Grid item>
            <Tooltip title="Back" arrow>
              <ArrowBackIosIcon
                sx={{
                  cursor: "pointer",
                }}
                onClick={() => {
                  navigate(routes.DASHBOARD);
                }}
              />
            </Tooltip>
          </Grid>
        )}

        {isAuthenticated && !emailReceivedDone && (
          <Grid m={2} item sx={{ position: "absolute", top: 10, right: 10 }}>
            <Tooltip title="Edit" arrow>
              <Button
                onClick={() => {
                  navigate(`/edit-form/${id}`);
                }}
                startIcon={<EditIcon sx={{ color: "white" }} />}
                size="large"
                sx={{
                  backgroundColor: "black",
                  color: "white",
                  "&:hover": {
                    backgroundColor: "#333",
                  },
                  fontWeight: 600,
                  width: "150px",
                }}
              >
                Edit
              </Button>
            </Tooltip>{" "}
          </Grid>
        )}

        {showLoader([loading])}

        {/* Form Header */}
        <motion.div
          initial={{ filter: "blur(20px)", opacity: 0 }}
          animate={{ filter: "blur(0px)", opacity: 1 }}
          transition={{ duration: 1 }}
          className="text-xl text-center sm:text-4xl font-bold tracking-tighter md:text-6xl md:leading-[4rem]"
        >
          <FormHeader />
        </motion.div>

        <Grid container spacing={3}>
          {/* License Information */}
          <ViewLicenseInformation data={formData} />

          {/* Store Information */}
          <ViewStoreInformation data={formData} />

          {/* Owner Information */}
          <OwnerInformation data={formData} />

          {/* Corporation Information */}
          <ViewCorporationInformation data={formData} />

          {/* Shopper Engagement */}
          <ViewShopperEngagement data={formData} />

          {/* Store Ad Group Information */}
          <ViewStoreAdGroupInformation data={formData} />

          {/* Insurance Information */}
          <ViewInsuranceInformation data={formData} />

          {/* Offerings at Store */}
          <ViewOfferingsAtStore data={formData} />

          {/* IGA Distribution Center */}
          <ViewIGADistributionCenter data={formData} />
        </Grid>
      </Paper>
    </motion.div>
  );
};

export default ViewRetailerApplicationForm;
